/**
 * SkinnrManager.ts - Skinnr - UI Skin/Theme Management System
 * 
 * CHANGE HISTORY:
 * - 251021_2025 [cc6aae8] danielbeer: fix: Resolve TypeScript migration errors
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
export default class Skinnr {
  /**
   * Create a new Skinnr instance
   * @param {string} namespace - Unique namespace for this tool (e.g., 'matchr', 'stormr')
   * @param {Object} options - Configuration options
   * @param {string} options.storage - Storage type: 'localStorage', 'memory', 'custom'
   * @param {string} options.defaultSkin - Default skin ID
   * @param {Function} options.customStorage - Custom storage implementation
   * @param {Function} options.onChange - Callback when skin changes
   */
  constructor(namespace: string, options: any = {}) {
    
    // Get Actr if provided, otherwise try to get it from Dependr
    this.actr = options.actr || this._tryGetActr();
    
    // Register actions with Actr if available
    if (this.actr) {
      this.registerActions();
    }
    this.namespace = namespace;
    this.options = {
      storage: 'localStorage',
      defaultSkin: 'default',
      customStorage: null,
      onChange: null,
      autoDetectDarkMode: true,
      supportDarkMode: true,
      ...options
    };

    // Registered skins
    this.skins = new Map();
    
    // Current active skin
    this.activeSkin = null;
    
    // Current mode (light/dark)
    this.currentMode = null;
    
    // Storage adapter
    this.storage = this.initStorage();
    
    // Event listeners
    this.listeners = new Set();
    
    // Load saved skin preference
    this.loadSavedSkin();
    
    // Setup dark mode detection
    if (this.options.autoDetectDarkMode) {
      this.setupDarkModeDetection();
    }
  }
  
  /**
   * Try to get Actr from Dependr (optional)
   */
  private _tryGetActr() {
    try {
      // Try to import and get Actr if available
      const { getActr } = require('@opcentral/actr');
      return getActr();
    } catch {
      // Actr not available, that's OK
      return null;
    }
  }
  
  /**
   * Register Skinnr actions with Actr
   * 
   * ARCHITECTURE NOTE: Skinnr uses HYBRID pattern
   * 
   * Why this is different from pure Actions-Only or Full CRUD:
   * 1. Theme Preferences: User preferences are persisted
   *    - Active skin stored in localStorage/database
   *    - Dark mode preference saved
   *    - Per-user theme settings
   *    - Needs CRUD for user preference entities
   * 
   * 2. Dynamic Theme Operations: Runtime theme switching
   *    - Apply skin (action - immediate visual change)
   *    - Toggle dark mode (action - runtime switch)
   *    - Register custom skins (action - runtime registration)
   *    - Actions needed for immediate UI updates
   * 
   * 3. Theme Entity Management: Skins are entities
   *    - Custom skins can be created/saved
   *    - Skin metadata (colors, variables, modes)
   *    - Skin relationships (variants, modes)
   *    - CRUD for custom skin entities
   * 
   * Pattern: Hybrid (Actions + Selective CRUD)
   * - Actions: For theme operations (apply, toggle, register)
   * - CRUD: For user preferences and custom skins
   * - Selective: Only user prefs and custom skins persisted
   * 
   * Compare to other patterns:
   * - authr: Hybrid (actions + selective CRUD for sessions)
   * - skinnr: Hybrid (actions + selective CRUD for preferences)
   * - notifyr: Full CRUD (all notifications persisted)
   * 
   * See: /docs/ACTR_INTEGRATION_PATTERNS.md for complete explanation
   * See: /packages/skinnr/README.md \"Actr Integration\" section
   */
  registerActions() {
    if (!this.actr) return;
    
    // applySkin action
    this.actr.register('skinnr:apply-skin', {
      handler: async (params) => {
        return this.applySkin(params.skinId, params.mode);
      }
    });
    
    // toggleDarkMode action
    this.actr.register('skinnr:toggle-dark-mode', {
      handler: async (params) => {
        return this.toggleDarkMode();
      }
    });
    
    // registerSkin action
    this.actr.register('skinnr:register-skin', {
      handler: async (params) => {
        return this.registerSkin(params.skinId, params.config);
      }
    });
    
    // TODO: Selective CRUD for user preferences
    // Note: registerCRUD method needs to be implemented in Actr
    /*
    this.actr.registerCRUD('skin-preference', {
      create: async (data) => {
        // Save user's skin preference
        const pref = {
          id: data.id || `${data.userId}_${this.namespace}`,
          userId: data.userId,
          namespace: this.namespace,
          skinId: data.skinId,
          mode: data.mode || 'light',
          customColors: data.customColors || {},
          createdAt: Date.now(),
          updatedAt: Date.now()
        };
        this.storage.set('preference', pref);
        return pref;
      },
      
      read: async (id) => {
        return this.storage.get('preference');
      },
      
      update: async (id, data) => {
        const existing = this.storage.get('preference') || {};
        const updated = {
          ...existing,
          ...data,
          updatedAt: Date.now()
        };
        this.storage.set('preference', updated);
        return updated;
      },
      
      delete: async (id) => {
        const existing = this.storage.get('preference');
        this.storage.set('preference', null);
        return existing;
      }
    });
    */
    
    // Note: Built-in skins are not persisted (registered at runtime).
    // Only user preferences and custom skins use CRUD.
  }

  /**
   * Initialize storage adapter
   */
  initStorage() {
    switch (this.options.storage) {
      case 'localStorage':
        return {
          get: (key) => {
            try {
              const data = localStorage.getItem(`skinnr_${this.namespace}_${key}`);
              return data ? JSON.parse(data) : null;
            } catch (e) {
              console.error('Skinnr: localStorage get error', e);
              return null;
            }
          },
          set: (key, value) => {
            try {
              localStorage.setItem(`skinnr_${this.namespace}_${key}`, JSON.stringify(value));
              return true;
            } catch (e) {
              console.error('Skinnr: localStorage set error', e);
              return false;
            }
          },
          remove: (key) => {
            try {
              localStorage.removeItem(`skinnr_${this.namespace}_${key}`);
              return true;
            } catch (e) {
              console.error('Skinnr: localStorage remove error', e);
              return false;
            }
          }
        };
      
      case 'memory':
        const memoryStore = new Map();
        return {
          get: (key) => memoryStore.get(key) || null,
          set: (key, value) => { memoryStore.set(key, value); return true; },
          remove: (key) => memoryStore.delete(key)
        };
      
      case 'custom':
        if (!this.options.customStorage) {
          throw new Error('Skinnr: customStorage option required when storage is "custom"');
        }
        return this.options.customStorage;
      
      default:
        throw new Error(`Skinnr: Unknown storage type "${this.options.storage}"`);
    }
  }

  /**
   * Register a new skin
   * @param {string} id - Unique skin ID
   * @param {Object} config - Skin configuration
   * @param {string} config.name - Display name
   * @param {string} config.description - Description
   * @param {Object} config.variables - CSS variables/design tokens
   * @param {Object} config.classes - CSS class mappings
   * @param {Object} config.layout - Layout configuration
   * @param {string} config.css - Custom CSS string
   * @param {Object} config.metadata - Additional metadata
   */
  registerSkin(id, config) {
    if (!id || typeof id !== 'string') {
      throw new Error('Skinnr: Skin ID must be a non-empty string');
    }

    const skin = {
      id,
      name: config.name || id,
      description: config.description || '',
      variables: config.variables || {},
      classes: config.classes || {},
      layout: config.layout || {},
      css: config.css || '',
      metadata: config.metadata || {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    this.skins.set(id, skin);
    
    // Save to storage
    this.saveSkins();
    
    return skin;
  }

  /**
   * Update an existing skin
   * @param {string} id - Skin ID
   * @param {Object} updates - Partial skin configuration to update
   */
  updateSkin(id, updates) {
    const skin = this.skins.get(id);
    if (!skin) {
      throw new Error(`Skinnr: Skin "${id}" not found`);
    }

    const updated = {
      ...skin,
      ...updates,
      id: skin.id, // Prevent ID change
      updatedAt: new Date().toISOString()
    };

    this.skins.set(id, updated);
    this.saveSkins();
    
    // Re-apply if this is the active skin
    if (this.activeSkin?.id === id) {
      this.applySkin(id);
    }
    
    return updated;
  }

  /**
   * Delete a skin
   * @param {string} id - Skin ID
   */
  deleteSkin(id) {
    if (this.activeSkin?.id === id) {
      this.applySkin(this.options.defaultSkin);
    }
    
    const deleted = this.skins.delete(id);
    if (deleted) {
      this.saveSkins();
    }
    
    return deleted;
  }

  /**
   * Get a skin by ID
   * @param {string} id - Skin ID
   */
  getSkin(id) {
    return this.skins.get(id);
  }

  /**
   * Get all registered skins
   */
  getAllSkins() {
    return Array.from(this.skins.values());
  }

  /**
   * Apply a skin
   * @param {string} id - Skin ID to apply
   * @param {HTMLElement} container - Optional container element
   */
  applySkin(id, container = document.documentElement) {
    const skin = this.skins.get(id);
    if (!skin) {
      console.warn(`Skinnr: Skin "${id}" not found, using default`);
      return false;
    }

    // Apply CSS variables
    if (skin.variables) {
      Object.entries(skin.variables).forEach(([key, value]) => {
        const cssVar = key.startsWith('--') ? key : `--${this.namespace}-${key}`;
        container.style.setProperty(cssVar, value);
      });
    }

    // Apply classes
    if (skin.classes && container.classList) {
      // Remove old skin classes
      if (this.activeSkin?.classes) {
        Object.values(this.activeSkin.classes).forEach(className => {
          if (className) container.classList.remove(className);
        });
      }
      
      // Add new skin classes
      Object.values(skin.classes).forEach(className => {
        if (className) container.classList.add(className);
      });
    }

    // Inject custom CSS
    this.injectCSS(skin);

    // Update active skin
    this.activeSkin = skin;
    
    // Save preference
    this.storage.set('activeSkin', id);
    
    // Trigger onChange callback
    if (this.options.onChange) {
      this.options.onChange(skin, this.activeSkin);
    }
    
    return true;
  }

  /**
   * Inject custom CSS for a skin
   */
  injectCSS(skin) {
    const styleId = `skinnr-${this.namespace}-${skin.id}`;
    
    // Remove old style element
    const oldStyle = document.getElementById(styleId);
    if (oldStyle) {
      oldStyle.remove();
    }

    // Inject new CSS if provided
    if (skin.css) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = skin.css;
      document.head.appendChild(style);
    }
  }

  /**
   * Get the currently active skin
   */
  getActiveSkin() {
    return this.activeSkin;
  }

  /**
   * Save skins to storage
   */
  saveSkins() {
    const skinsArray = Array.from(this.skins.values());
    this.storage.set('skins', skinsArray);
  }

  /**
   * Load skins from storage
   */
  loadSkins() {
    const skinsArray = this.storage.get('skins');
    if (skinsArray && Array.isArray(skinsArray)) {
      skinsArray.forEach(skin => {
        this.skins.set(skin.id, skin);
      });
    }
  }

  /**
   * Load saved skin preference and apply it
   */
  loadSavedSkin() {
    this.loadSkins();
    
    const savedSkinId = this.storage.get('activeSkin');
    const skinToApply = savedSkinId || this.options.defaultSkin;
    
    if (this.skins.has(skinToApply)) {
      this.applySkin(skinToApply);
    }
  }

  /**
   * Export skin configuration
   * @param {string} id - Skin ID
   * @returns {string} JSON string
   */
  exportSkin(id) {
    const skin = this.skins.get(id);
    if (!skin) {
      throw new Error(`Skinnr: Skin "${id}" not found`);
    }
    
    return JSON.stringify(skin, null, 2);
  }

  /**
   * Import skin from JSON
   * @param {string} json - JSON string
   * @param {boolean} overwrite - Overwrite if skin exists
   */
  importSkin(json, overwrite = false) {
    let skin;
    try {
      skin = JSON.parse(json);
    } catch (e) {
      throw new Error('Skinnr: Invalid JSON');
    }

    if (!skin.id) {
      throw new Error('Skinnr: Skin must have an ID');
    }

    if (this.skins.has(skin.id) && !overwrite) {
      throw new Error(`Skinnr: Skin "${skin.id}" already exists`);
    }

    this.registerSkin(skin.id, skin);
    return skin;
  }

  /**
   * Clone a skin with a new ID
   * @param {string} sourceId - Source skin ID
   * @param {string} newId - New skin ID
   * @param {string} newName - New skin name
   */
  cloneSkin(sourceId, newId, newName) {
    const source = this.skins.get(sourceId);
    if (!source) {
      throw new Error(`Skinnr: Source skin "${sourceId}" not found`);
    }

    const clone = {
      ...source,
      id: newId,
      name: newName || `${source.name} (Copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    this.registerSkin(newId, clone);
    return clone;
  }

  /**
   * Reset to default skin
   */
  reset() {
    this.applySkin(this.options.defaultSkin);
  }

  /**
   * Clear all skins and reset
   */
  clearAll() {
    this.skins.clear();
    this.activeSkin = null;
    this.storage.remove('skins');
    this.storage.remove('activeSkin');
  }

  /**
   * Setup dark mode detection
   */
  setupDarkModeDetection() {
    if (typeof window === 'undefined') return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      const prefersDark = e.matches;
      this.currentMode = prefersDark ? 'dark' : 'light';
      
      // Auto-switch to dark/light skin if available
      if (this.options.supportDarkMode) {
        const targetSkin = this.findSkinByMode(this.currentMode);
        if (targetSkin) {
          this.applySkin(targetSkin.id);
        }
      }
      
      this.notifyListeners('modeChanged', this.currentMode);
    };
    
    // Initial check
    handleChange(mediaQuery);
    
    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
  }

  /**
   * Find skin by mode (light/dark)
   */
  findSkinByMode(mode) {
    return Array.from(this.skins.values()).find(skin => 
      skin.metadata?.mode === mode || skin.mode === mode
    );
  }

  /**
   * Get current mode
   */
  getCurrentMode() {
    return this.currentMode;
  }

  /**
   * Toggle between light and dark mode
   */
  toggleMode() {
    const targetMode = this.currentMode === 'dark' ? 'light' : 'dark';
    const targetSkin = this.findSkinByMode(targetMode);
    
    if (targetSkin) {
      this.applySkin(targetSkin.id);
      this.currentMode = targetMode;
      return true;
    }
    
    return false;
  }

  /**
   * Subscribe to events
   */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify listeners
   */
  notifyListeners(event, ...args) {
    this.listeners.forEach(listener => {
      try {
        listener(event, ...args);
      } catch (error) {
        console.error('Skinnr listener error:', error);
      }
    });
  }

  /**
   * Generate CSS from skin
   */
  generateCSS(skinId, selector = ':root') {
    const skin = this.skins.get(skinId);
    if (!skin) return '';
    
    let css = `${selector} {\n`;
    
    // Add CSS variables
    if (skin.variables) {
      Object.entries(skin.variables).forEach(([key, value]) => {
        const cssVar = key.startsWith('--') ? key : `--${this.namespace}-${key}`;
        css += `  ${cssVar}: ${value};\n`;
      });
    }
    
    css += `}\n`;
    
    // Add custom CSS
    if (skin.css) {
      css += `\n${skin.css}\n`;
    }
    
    return css;
  }

  /**
   * Batch update multiple skins
   */
  batchUpdate(updates) {
    const results = [];
    
    updates.forEach(({ id, config }) => {
      try {
        if (this.skins.has(id)) {
          results.push(this.updateSkin(id, config));
        } else {
          results.push(this.registerSkin(id, config));
        }
      } catch (error) {
        console.error(`Skinnr batch update error for ${id}:`, error);
      }
    });
    
    return results;
  }

  /**
   * Get skin statistics
   */
  getStats() {
    const skins = Array.from(this.skins.values());
    
    return {
      total: skins.length,
      active: this.activeSkin?.id || null,
      modes: {
        light: skins.filter(s => s.mode === 'light' || s.metadata?.mode === 'light').length,
        dark: skins.filter(s => s.mode === 'dark' || s.metadata?.mode === 'dark').length
      },
      hasCustomCSS: skins.filter(s => s.css).length,
      hasVariables: skins.filter(s => Object.keys(s.variables || {}).length > 0).length
    };
  }

  /**
   * Validate skin configuration
   */
  validateSkin(config) {
    const errors = [];
    
    if (!config.id) errors.push('Skin must have an ID');
    if (!config.name) errors.push('Skin must have a name');
    
    if (config.variables && typeof config.variables !== 'object') {
      errors.push('Variables must be an object');
    }
    
    if (config.classes && typeof config.classes !== 'object') {
      errors.push('Classes must be an object');
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Merge skins (combine variables and classes)
   */
  mergeSkins(baseId, overlayId, newId, newName) {
    const base = this.skins.get(baseId);
    const overlay = this.skins.get(overlayId);
    
    if (!base || !overlay) {
      throw new Error('Both base and overlay skins must exist');
    }
    
    const merged = {
      id: newId,
      name: newName || `${base.name} + ${overlay.name}`,
      description: `Merged from ${base.name} and ${overlay.name}`,
      variables: {
        ...base.variables,
        ...overlay.variables
      },
      classes: {
        ...base.classes,
        ...overlay.classes
      },
      layout: {
        ...base.layout,
        ...overlay.layout
      },
      css: [base.css, overlay.css].filter(Boolean).join('\n\n'),
      metadata: {
        ...base.metadata,
        ...overlay.metadata,
        mergedFrom: [baseId, overlayId]
      }
    };
    
    return this.registerSkin(newId, merged);
  }
}
